import {useEffect, useRef, useState} from "react";
import {Server} from "../../api/types";
import Loading from "../Loading";
import {agent} from "../../api/agent";
import styled from "styled-components";
import Button from "../Button";
import {useNavigate} from "react-router-dom";
import {Modal} from "../Modal";
import Input from "../Input";
import ServerTokens from "./ServerTokens";

interface Props {
    serverId: string;
}

const Center = styled.div`
    display: flex;
    justify-content: center;
`;

const Content = styled.div`

    display: flex;
    flex-direction: column;
    gap: 1rem;
    background-color: var(--content-box-bg);
    padding: 1rem;
    margin-top: 1rem;
    min-width: 40rem;

    .label {
        font-weight: bold;
        display: block;
        margin-bottom: 0.5rem;
    }

    .grid {
        display: grid;
        grid-template-columns: 1fr 1fr;
        gap: 1rem;
    }

    input {
        background-color: transparent;
        outline: none;
        border: none;
        font-size: 1.1rem;
        margin: .2rem .5rem;
        padding: .2rem;
        color: #f2f2f2;
        border-bottom: #f2f2f2 solid 1px;
    }
`;

export default function ServerEditor({serverId}: Props) {

    const [server, setServer] = useState<Server | null>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const [passwordModalOpen, setPasswordModalOpen] = useState<boolean>(false);

    const navigate = useNavigate();

    useEffect(() => {
        setPasswordModalOpen(false);
        setLoading(true);
        if (serverId !== 'new') {
            agent.getServer(serverId).then(value => {
                setServer(value);
                nameRef.current!.value = value.name;
                hostRef.current!.value = value.host;
                portRef.current!.value = value.port.toString();
                usernameRef.current!.value = value.username;
                canBeUsedForCIRef.current!.checked = value.canBeUsedForCI;
                concurrentJobsRef.current!.value = value.concurrentJobs.toString();
                setCanBeUsedForCI(value.canBeUsedForCI);
                setLoading(false);
            });
        } else {
            setLoading(false);
        }
    }, [serverId]);

    const nameRef = useRef<HTMLInputElement>(null);
    const hostRef = useRef<HTMLInputElement>(null);
    const portRef = useRef<HTMLInputElement>(null);
    const usernameRef = useRef<HTMLInputElement>(null);
    const passwordRef = useRef<HTMLInputElement>(null);
    const passwordModelRef = useRef<HTMLInputElement>(null);
    const canBeUsedForCIRef = useRef<HTMLInputElement>(null);
    const concurrentJobsRef = useRef<HTMLInputElement>(null);
    const [changedPassword, setChangedPassword] = useState<string | null>('');

    const save = () => {
        setLoading(true);
        if (!server) {
            const newServer: Server = {
                id: '',
                name: nameRef.current?.value || '',
                host: hostRef.current?.value || '',
                port: parseInt(portRef.current?.value || '0'),
                username: usernameRef.current?.value || '',
                password: passwordRef.current?.value || '',
                canBeUsedForCI: canBeUsedForCIRef.current?.checked || false,
                concurrentJobs: parseInt(concurrentJobsRef.current?.value || '1'),
            };
            agent.postServer(newServer).then(value => {
                navigate('/servers');
                setLoading(false);
            });
        }else{
            const updatedServer: Server = {
                ...server,
                name: nameRef.current?.value || server.name,
                host: hostRef.current?.value || server.host,
                port: parseInt(portRef.current?.value || server.port.toString()),
                username: usernameRef.current?.value || server.username,
                canBeUsedForCI: canBeUsedForCIRef.current?.checked || server.canBeUsedForCI,
                concurrentJobs: parseInt(concurrentJobsRef.current?.value || server.concurrentJobs.toString()),
            };
            if (changedPassword) {
                updatedServer.password = changedPassword;
            }
            agent.putServer(updatedServer).then(value => {
                navigate('/servers');
                setLoading(false);
            });
        }
    }

    const deleteServer = () => {
        agent.deleteServer(serverId).then(() => {
            navigate('/servers');
        });
    }

    const [canBeUsedForCI, setCanBeUsedForCI] = useState<boolean>(server?.canBeUsedForCI || false);

    return (
        <>
            <Modal open={passwordModalOpen} onClose={() => setPasswordModalOpen(false)}>
                <h1>Set Password</h1>
                <Input placeholder={'Enter new password'} ref={passwordModelRef}/>
                <div style={{display: "flex", justifyContent: "flex-end", gap: "1rem", marginTop: '.5rem'}}>
                    <Button className={'primary'} onClick={() => {
                        setChangedPassword(passwordModelRef.current!.value);
                        setPasswordModalOpen(false);
                    }}>Save</Button>
                </div>
            </Modal>
            <Loading open={loading}></Loading>
            <Center>
                <Content>
                    <div>
                        <span className={'label'}>Name</span>
                        <input placeholder={'Enter server name'} ref={nameRef}/>
                    </div>
                    <div className={'grid'}>
                        <div>
                            <span className={'label'}>Host</span>
                            <input placeholder={'Enter host'} ref={hostRef}/>
                        </div>
                        <div>
                            <span className={'label'}>Port</span>
                            <input placeholder={'Enter port'} type={'number'} ref={portRef}/>
                        </div>
                    </div>
                    <div className={'grid'}>
                        <div>
                            <span className={'label'}>Username</span>
                            <input placeholder={'Enter username'} ref={usernameRef}/>
                        </div>
                        {!server && (
                            <div>
                                <span className={'label'}>Password</span>
                                <input placeholder={'Enter password'} ref={passwordRef}/>
                            </div>
                        )}
                        {server && (
                            <div>
                                <Button onClick={() => setPasswordModalOpen(true)}>Set Password</Button>
                            </div>
                        )}
                    </div>
                    <div className={'grid'}>
                        <div>
                            <span className={'label'}>Can be used for CI</span>
                            <input type={'checkbox'} defaultChecked={server?.canBeUsedForCI || false} ref={canBeUsedForCIRef} onChange={(ev) => {
                                setCanBeUsedForCI(ev.target.checked);
                            }}/>
                            <input placeholder={'Concurrent Jobs'} type={'number'} defaultValue={server?.concurrentJobs.toString() || '1'} ref={concurrentJobsRef} disabled={!canBeUsedForCI}/>
                        </div>
                    </div>
                    {server && (<ServerTokens serverId={serverId}></ServerTokens>)}
                    <div style={{display: 'flex', gap: '1rem', justifyContent: 'flex-end'}}>
                        <Button className={'default'} onClick={() => {navigate('/servers')}}>Cancel</Button>
                        <Button className={'red'} onClick={deleteServer}>Delete</Button>
                        <Button className={'primary'} onClick={save}>Save</Button>
                    </div>
                </Content>
            </Center>
        </>
    );
}

